// screen: Asset — Overview
import * as React from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useLingui, Trans } from '@lingui/react/macro';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Copy, Download, Trash2, Check, CircleDashed, CircleX, ImageIcon } from 'lucide-react';
import { ApiError, qk, type Schemas } from '@viewstream/api-client';
import { Badge, Button, ConfirmByNameDialog, EmptyState, ErrorState, Field, Input, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger, cn, useToast } from '@viewstream/ui';
import { Player, type VsPlayer } from '@viewstream/player-react';
import { fmtDateTime, fmtDuration } from '@viewstream/i18n';
import { api } from '~/app/api';
import { appTitle } from '~/app/brand';
import { useTenant } from '~/app/tenant';
import { useLocale } from '~/app/locale';
import { CINEMA_PLAYER, CINEMA_STAGE } from '~/app/cinema';
import { can } from '~/app/permissions';
import { EmbedShare } from '~/app/EmbedShare';
import { PresetPicker } from '~/app/PresetPicker';
import { AssetAds } from '~/app/AssetAds';
import { AssetVodAds } from '~/app/VodAds';
import { AssetPackaging } from '~/app/Packaging';
import { PosterPicker } from '~/app/PosterPicker';
import { AiOverlay, UpscalePanel } from '~/app/ImageUpscale';
import { VideoUpscalePanel } from '~/app/VideoUpscale';
import { AssetWatermark } from '~/app/Watermark';
import { LipsyncScanPanel } from '~/app/LipsyncScan';
import { pathFromUrl } from '~/app/upscale';
import { usePoster } from '~/app/posters';
import { AssetStatusBadge, PIPELINE, useAssetStatusLabel, useDelete, usePublish } from '~/app/assets';
import { SubtitleLanguages } from '~/app/Subtitles';
import { ContentSummaryPanel } from '~/app/ContentSummary';
import { AdCategoriesPanel } from '~/app/ContextualAds';
import { AssetRenditions } from '~/app/AssetRenditions';
import { AssetStats } from '~/app/ContentStats';
import { VideoId } from '~/app/VideoId';
import { ClipEditor, type ClipEditorSource } from '~/app/ClipEditor';
import { useChannel } from '~/app/live';

type Tab = 'overview' | 'stats' | 'metadata' | 'publishing' | 'ads' | 'embed' | 'history';
const TABS: Tab[] = ['overview', 'stats', 'metadata', 'publishing', 'ads', 'embed', 'history'];

export const Route = createFileRoute('/_layout/t/$slug/library/$assetId')({
  component: Screen,
  validateSearch: (s: Record<string, unknown>): { tab?: Tab } => (TABS.includes(s['tab'] as Tab) ? { tab: s['tab'] as Tab } : {}),
});

type Asset = Schemas['Asset'];
interface JobLite { id?: string; type?: string; status?: string; created_at?: string; started_at?: string | null; finished_at?: string | null; error?: string | null; attempts?: number }
interface AssetMeta { description?: string; tags?: string[]; category?: string; language?: string; [k: string]: unknown }


function Screen() {
  const { t } = useLingui();
  const { slug, assetId } = Route.useParams();
  const { tab = 'overview' } = Route.useSearch();
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const q = useQuery({ queryKey: qk.asset(tenant.slug, assetId), queryFn: async () => (await api.GET('/v1/assets/{id}', { params: { path: { id: assetId } } })).data as Asset });
  React.useEffect(() => { document.title = appTitle(q.data?.title || t`Asset`); }, [t, q.data?.title]);
  const setTab = (v: string) => void navigate({ to: '/t/$slug/library/$assetId', params: { slug, assetId }, search: { tab: v as Tab }, replace: true } as never);

  if (q.isPending) return <div className="space-y-3"><Skeleton className="h-8 w-72" /><Skeleton className="h-64 w-full" /></div>;
  if (q.isError) {
    if (q.error instanceof ApiError && q.error.isNotFound) return <EmptyState title={t`Asset not found`} description={t`It may have been moved to trash.`} actions={<BackLink />} />;
    return <ErrorState error={q.error as ApiError} onRetry={() => q.refetch()} labels={{ title: t`Could not load the asset`, retry: t`Retry`, requestId: t`Request id`, copy: t`Copy` }} />;
  }
  const a = q.data;
  return (
    <div>
      <div className="mb-2"><BackLink /></div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold">{a.title || t`(untitled)`}</h1>
        <AssetStatusBadge status={a.status} />
        {a.published_at ? <Badge tone="ok">{t`Published`}</Badge> : <Badge>{t`Draft`}</Badge>}
        <VideoId id={a.id} full />
        <AssetClipEditorButton a={a} />
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview">{t`Overview`}</TabsTrigger>
          <TabsTrigger value="stats">{t`Statistics`}</TabsTrigger>
          <TabsTrigger value="metadata">{t`Metadata`}</TabsTrigger>
          <TabsTrigger value="publishing">{t`Publishing`}</TabsTrigger>
          <TabsTrigger value="ads">{t`Ads`}</TabsTrigger>
          <TabsTrigger value="embed">{t`Embed`}</TabsTrigger>
          <TabsTrigger value="history">{t`History`}</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="pt-4"><Overview a={a} /></TabsContent>
        <TabsContent value="stats" className="pt-4">{tab === 'stats' ? <AssetStats assetId={a.id!} /> : null}</TabsContent>
        <TabsContent value="metadata" className="pt-4"><Metadata a={a} /></TabsContent>
        <TabsContent value="publishing" className="pt-4"><Publishing a={a} /></TabsContent>
        <TabsContent value="ads" className="pt-4">{tab === 'ads' ? <AssetVodAds assetId={a.id!} /> : null}</TabsContent>
        <TabsContent value="embed" className="pt-4"><Embed a={a} /></TabsContent>
        <TabsContent value="history" className="pt-4"><History a={a} /></TabsContent>
      </Tabs>
    </div>
  );
  function BackLink() {
    return <Link to={'/t/$slug/library' as never} params={{ slug } as never} className="inline-flex items-center gap-1 text-sm text-fg-muted hover:text-fg"><ArrowLeft className="size-4 icon-dir" aria-hidden /> {t`Library`}</Link>;
  }
}

function Overview({ a }: { a: Asset }) {
  const { t } = useLingui();
  const { locale, tz } = useLocale();
  const { scopes } = useTenant();
  const label = useAssetStatusLabel();
  const failed = a.status === 'failed';
  const reached = PIPELINE.indexOf((a.status ?? 'registered') as never);
  const probe = (a.probe ?? null) as Record<string, unknown> | null;
  const canMaster = can(scopes, 'channels:write'); // Engineer and above (Studio spec §4.3: mezzanine for Owners/Engineers)
  const [cinema, setCinema] = React.useState(false);
  const vp = React.useRef<VsPlayer | null>(null);
  return (
    // cinema mode (player button / T): the player spans the page, the details column moves below it
    <div className={cinema ? 'grid gap-6' : 'grid gap-6 lg:grid-cols-3'} data-cinema={cinema || undefined}>
      <div className={cinema ? 'space-y-4' : 'space-y-4 lg:col-span-2'}>
        <div className={cinema ? CINEMA_STAGE : undefined}>
          <Player mode="main" poster={a.playback?.poster ?? undefined} src={a.playback?.hls ?? undefined} label={t`Player for ${a.title ?? ''}`}
            cinema onCinema={setCinema} style={cinema ? CINEMA_PLAYER : undefined} onReady={(p) => { vp.current = p; }} />
        </div>
        <section aria-label={t`Pipeline`}>
          <h2 className="mb-2 text-sm font-semibold">{t`Pipeline`}</h2>
          <ol className="flex flex-wrap items-center gap-2 text-sm" data-testid="pipeline">
            {PIPELINE.map((s, i) => {
              const state = failed && i === Math.max(0, reached) ? 'failed' : i < reached || a.status === 'ready' ? 'done' : i === reached ? 'current' : 'todo';
              return (
                <li key={s} data-state={state} className={cn('flex items-center gap-1 rounded-full border px-2 py-0.5', state === 'done' && 'border-ok/30 text-ok', state === 'current' && 'border-primary text-primary', state === 'failed' && 'border-danger text-danger', state === 'todo' && 'border-border text-fg-muted')}>
                  {state === 'done' ? <Check className="size-3.5" aria-hidden /> : state === 'failed' ? <CircleX className="size-3.5" aria-hidden /> : <CircleDashed className="size-3.5" aria-hidden />} {label(s)}
                </li>
              );
            })}
          </ol>
          {a.error ? <p role="alert" className="mt-2 text-sm text-danger">{a.error}</p> : null}
          <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            <dt className="text-fg-muted">{t`Registered`}</dt><dd>{fmtDateTime(a.created_at!, { locale, tz })}</dd>
            <dt className="text-fg-muted">{t`Ready`}</dt><dd>{a.ready_at ? fmtDateTime(a.ready_at, { locale, tz }) : '—'}</dd>
            <dt className="text-fg-muted">{t`Duration`}</dt><dd className="ltr-isolate">{a.duration_ms ? fmtDuration(a.duration_ms) : '—'}</dd>
            <dt className="text-fg-muted">{t`Ladder`}</dt><dd className="ltr-isolate">{a.ladder}</dd>
          </dl>
        </section>
        <AssetRenditions a={a} />
      </div>
      <aside className="space-y-4">
        <section aria-label={t`Downloads`}>
          <h2 className="mb-2 text-sm font-semibold">{t`Downloads`}</h2>
          <ul className="space-y-1 text-sm">
            {a.playback?.download ? <li><a className="inline-flex items-center gap-1 text-primary hover:underline" href={a.playback.download} download><Download className="size-4" aria-hidden /> {t`Progressive MP4`}</a></li> : <li className="text-fg-muted">{t`Available when the asset is ready.`}</li>}
            {canMaster && a.master_key ? <li className="text-fg-muted"><Trans>Mezzanine: <span className="ltr-isolate">{a.master_key}</span></Trans></li> : null}
          </ul>
        </section>
        <section aria-label={t`Source file`}>
          <h2 className="mb-2 text-sm font-semibold">{t`Source file`}</h2>
          {probe ? <ProbeSummary probe={probe} /> : <p className="text-sm text-fg-muted">{t`Probe details appear after probing.`}</p>}
        </section>
        {a.status === 'ready' ? <AssetPoster a={a} canWrite={can(scopes, 'assets:write')} /> : null}
        {a.status === 'ready' && a.id ? <SubtitleLanguages subjectId={a.id} /> : null}
        {a.status === 'ready' && a.id ? <ContentSummaryPanel kind="asset" id={a.id} /> : null}
        {a.status === 'ready' && a.id ? <AdCategoriesPanel type="asset" id={a.id} onSeek={(ms) => { vp.current?.currentTime(ms / 1000); }} /> : null}
        {a.status === 'ready' && a.id ? <VideoUpscalePanel assetId={a.id} canWrite={can(scopes, 'assets:write')} /> : null}
        {a.status === 'ready' && a.id ? <AssetWatermark assetId={a.id} /> : null}
        {a.status === 'ready' && a.id ? <LipsyncScanPanel assetId={a.id} onSeek={(ms) => { vp.current?.currentTime(ms / 1000); }} /> : null}
      </aside>
    </div>
  );
}

function AssetPoster({ a, canWrite }: { a: Asset; canWrite: boolean }) {
  const { t } = useLingui();
  const [open, setOpen] = React.useState(false);
  const q = usePoster('asset', a.id);
  const src = q.data?.current?.url ?? a.playback?.poster ?? undefined;
  const imgPath = q.data?.current?.path ?? pathFromUrl(a.playback?.poster);
  return (
    <section aria-labelledby="poster-h" className="space-y-2">
      <h2 id="poster-h" className="text-sm font-semibold">{t`Poster`}</h2>
      {src ? (
        <div className="relative">
          <img src={src} alt="" className="aspect-video w-full rounded-md bg-surface-2 object-cover" />
          <AiOverlay path={imgPath} />
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-2 text-xs text-fg-muted">
        <span>{q.data?.mode === 'editor' ? t`Chosen by an editor` : q.data?.mode === 'auto' ? t`Chosen automatically` : t`First frame`}</span>
        <Button size="sm" variant="secondary" className="ms-auto" onClick={() => setOpen(true)} data-testid="choose-poster"><ImageIcon className="size-4" aria-hidden />{t`Choose poster`}</Button>
      </div>
      {imgPath ? <UpscalePanel path={imgPath} canWrite={canWrite} compact /> : null}
      <PosterPicker type="asset" id={a.id!} title={a.title ?? ''} open={open} onOpenChange={setOpen} canWrite={canWrite} />
    </section>
  );
}

function ProbeSummary({ probe }: { probe: Record<string, unknown> }) {
  const { t } = useLingui();
  const v = (probe['video'] ?? (probe['streams'] as unknown[] | undefined)?.[0] ?? {}) as Record<string, unknown>;
  const rows: [string, unknown][] = [
    [t`Container`, probe['format'] ?? probe['format_name']],
    [t`Video codec`, v['codec'] ?? v['codec_name']],
    [t`Resolution`, v['width'] && v['height'] ? `${v['width']}×${v['height']}` : undefined],
    [t`Frame rate`, v['fps'] ?? v['r_frame_rate']],
    [t`Interlaced`, v['interlaced'] === undefined ? undefined : v['interlaced'] ? t`yes` : t`no`],
  ];
  return (
    <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
      {rows.filter(([, val]) => val !== undefined && val !== null && val !== '').map(([k, val]) => (
        <React.Fragment key={k}><dt className="text-fg-muted">{k}</dt><dd className="ltr-isolate">{String(val)}</dd></React.Fragment>
      ))}
    </dl>
  );
}

function Metadata({ a }: { a: Asset }) {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const toast = useToast();
  const qc = useQueryClient();
  const meta = (a.metadata ?? {}) as AssetMeta;
  const [title, setTitle] = React.useState(a.title ?? '');
  const [ext, setExt] = React.useState(a.external_id ?? '');
  const [desc, setDesc] = React.useState(meta.description ?? '');
  const [tags, setTags] = React.useState((meta.tags ?? []).join(', '));
  const [category, setCategory] = React.useState(meta.category ?? '');
  const [language, setLanguage] = React.useState(meta.language ?? '');
  const canWrite = can(scopes, 'assets:write');
  const save = useMutation({
    mutationFn: async () => {
      const metadata: AssetMeta = { ...meta, description: desc.trim() || undefined, tags: tags.split(',').map((x) => x.trim()).filter(Boolean), category: category.trim() || undefined, language: language.trim() || undefined };
      return (await api.PATCH('/v1/assets/{id}', { params: { path: { id: a.id! } }, body: { title: title.trim(), external_id: ext.trim() || undefined, metadata } as never })).data as Asset;
    },
    onSuccess: (updated) => {
      qc.setQueryData(qk.asset(tenant.slug, a.id!), updated);
      void qc.invalidateQueries({ queryKey: qk.assets(tenant.slug) });
      toast.push({ title: t`Metadata saved`, tone: 'ok' });
    },
    onError: (e) => toast.push({ title: t`Could not save`, description: e instanceof ApiError ? e.problem?.detail : String(e), tone: 'danger' }),
  });
  return (
    <form className="max-w-2xl space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }} aria-label={t`Metadata`}>
      <fieldset disabled={!canWrite} className="space-y-4">
        <Field id="m-title" label={t`Title`}><Input value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={500} /></Field>
        <Field id="m-desc" label={t`Description`}>
          <textarea id="m-desc" rows={4} value={desc} onChange={(e) => setDesc(e.target.value)} className="w-full rounded-md border border-border bg-surface p-2 text-sm" />
        </Field>
        <Field id="m-tags" label={t`Tags`} hint={t`Comma separated`}><Input value={tags} onChange={(e) => setTags(e.target.value)} /></Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="m-cat" label={t`Category`}><Input value={category} onChange={(e) => setCategory(e.target.value)} /></Field>
          <Field id="m-lang" label={t`Language`} hint={t`e.g. he, en`}><Input value={language} onChange={(e) => setLanguage(e.target.value)} dir="ltr" /></Field>
        </div>
        <Field id="m-ext" label={t`External id`} hint={t`Your CMS id; unique per tenant`}><Input value={ext} onChange={(e) => setExt(e.target.value)} dir="ltr" /></Field>
      </fieldset>
      {canWrite ? <Button type="submit" loading={save.isPending}>{t`Save metadata`}</Button> : <p className="text-sm text-fg-muted">{t`Your role can view metadata but not change it.`}</p>}
    </form>
  );
}

function Publishing({ a }: { a: Asset }) {
  const { t } = useLingui();
  const { locale, tz } = useLocale();
  const { tenant, scopes } = useTenant();
  const navigate = useNavigate();
  const toast = useToast();
  const publish = usePublish(tenant.slug);
  const del = useDelete(tenant.slug);
  const [confirm, setConfirm] = React.useState(false);
  const canPublish = can(scopes, 'assets:publish');
  const canWrite = can(scopes, 'assets:write');
  const published = !!a.published_at;
  return (
    <div className="max-w-2xl space-y-6">
      <section className="space-y-2">
        <h2 className="text-sm font-semibold">{t`State`}</h2>
        <p className="text-sm" data-testid="publish-state">
          {published ? <Trans>Published {fmtDateTime(a.published_at!, { locale, tz })}</Trans> : a.status === 'ready' ? t`Ready, not published` : t`Draft — publishing is possible once the asset is ready`}
        </p>
        {canPublish ? (
          <Button variant={published ? 'secondary' : 'primary'} loading={publish.isPending} disabled={!published && a.status !== 'ready'}
            onClick={() => publish.mutate({ ids: [a.id!], publish: !published }, {
              onSuccess: () => toast.push({ title: published ? t`Unpublished` : t`Published`, tone: 'ok' }),
              onError: (e) => toast.push({ title: t`Could not change publishing`, description: e instanceof ApiError ? e.problem?.detail : String(e), tone: 'danger' }),
            })}>
            {published ? t`Unpublish` : t`Publish`}
          </Button>
        ) : <p className="text-sm text-fg-muted">{t`Publishing needs the Publisher role.`}</p>}
      </section>
      <section className="space-y-2" aria-labelledby="pub-preset-h">
        <h2 id="pub-preset-h" className="text-sm font-semibold">{t`Player`}</h2>
        <PresetPicker tenant={tenant.slug} scope="asset" targetId={a.id!} kind="vod" contentId={a.id!} canWrite={can(scopes, 'delivery:write')} idPrefix="asset-preset" />
      </section>
      <AssetAds a={a} tenant={tenant.slug} canWrite={canWrite} />
      <AssetPackaging assetId={a.id!} />
      <section className="space-y-1">
        <h2 className="text-sm font-semibold">{t`Schedule, visibility and expiry`}</h2>
        <p className="text-sm text-fg-muted">{t`Scheduled publishing, token-required visibility, geo presets and expiry need control-plane routes that are not built yet (studio/docs/api-gaps.md).`}</p>
      </section>
      {canWrite ? (
        <section className="space-y-2 rounded-lg border border-danger/30 p-4">
          <h2 className="text-sm font-semibold text-danger">{t`Move to trash`}</h2>
          <p className="text-sm text-fg-muted">{t`Renditions are purged from the library; the master is kept. Restoring from trash is not available yet.`}</p>
          <Button variant="danger" onClick={() => setConfirm(true)}><Trash2 className="size-4" aria-hidden /> {t`Move to trash`}</Button>
          <ConfirmByNameDialog open={confirm} onOpenChange={setConfirm} name={a.title || a.id!} title={t`Move “${a.title || a.id}” to trash?`}
            description={t`Type the asset name to confirm.`} busy={del.isPending}
            labels={{ field: t`Asset name`, confirm: t`Move to trash`, cancel: t`Cancel`, close: t`Close` }}
            onConfirm={() => del.mutateAsync([a.id!]).then(() => { toast.push({ title: t`Moved to trash`, tone: 'ok' }); void navigate({ to: '/t/$slug/library', params: { slug: tenant.slug } } as never); })} />
        </section>
      ) : null}
    </div>
  );
}

function CopyField({ id, label, value }: { id: string; label: string; value: string }) {
  const { t } = useLingui();
  const [copied, setCopied] = React.useState(false);
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="text-sm font-medium">{label}</label>
      <div className="flex gap-2">
        <textarea id={id} readOnly dir="ltr" rows={value.includes('\n') ? 3 : 1} value={value} className="w-full resize-none rounded-md border border-border bg-surface-2 p-2 font-mono text-xs" />
        <Button variant="secondary" size="icon" aria-label={t`Copy ${label}`} onClick={() => { void navigator.clipboard?.writeText(value).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }); }}>
          {copied ? <Check className="size-4" aria-hidden /> : <Copy className="size-4" aria-hidden />}
        </Button>
      </div>
    </div>
  );
}

function Embed({ a }: { a: Asset }) {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const pb = a.playback ?? {};
  return (
    <div className="grid max-w-4xl gap-6">
      {a.status !== 'ready' ? <p role="status" className="text-sm text-warn">{t`The asset is not ready yet; embeds play once encoding finishes.`}</p> : null}
      <EmbedShare tenant={tenant.slug} kind="vod" id={a.id!} src={pb.hls} title={a.title} idPrefix="asset-es" />
      <section className="space-y-4">
        <h2 className="text-sm font-semibold">{t`Playback URLs`}</h2>
        {pb.hls ? <CopyField id="e-hls" label="HLS" value={pb.hls} /> : null}
        {pb.poster ? <CopyField id="e-poster" label={t`Poster`} value={pb.poster} /> : null}
        {pb.thumbs_vtt ? <CopyField id="e-vtt" label={t`Thumbnails VTT`} value={pb.thumbs_vtt} /> : null}
        {pb.download ? <CopyField id="e-mp4" label="MP4" value={pb.download} /> : null}
        {!pb.hls ? <p className="text-sm text-fg-muted">{t`Playback URLs appear when the asset is ready.`}</p> : null}
      </section>
    </div>
  );
}

function History({ a }: { a: Asset }) {
  const { t } = useLingui();
  const { locale, tz } = useLocale();
  const jobs = ((a.jobs ?? []) as JobLite[]).slice().sort((x, y) => String(x.created_at).localeCompare(String(y.created_at)));
  const [openJob, setOpenJob] = React.useState<string | null>(null);
  return (
    <div className="max-w-3xl space-y-4">
      <p className="text-sm text-fg-muted">{t`Pipeline jobs of this asset. Metadata versions and the audit trail need control-plane routes that are not built yet (studio/docs/api-gaps.md).`}</p>
      {jobs.length ? (
        <ol className="space-y-2">
          {jobs.map((j) => (
            <li key={j.id} className="rounded-md border border-border p-3 text-sm" data-job-type={j.type}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium ltr-isolate">{j.type}</span>
                <Badge tone={j.status === 'succeeded' ? 'ok' : j.status === 'failed' ? 'danger' : 'primary'}>{j.status}</Badge>
              </div>
              <p className="mt-1 text-xs text-fg-muted">
                {j.created_at ? fmtDateTime(j.created_at, { locale, tz }) : ''}{j.finished_at ? ` → ${fmtDateTime(j.finished_at, { locale, tz })}` : ''}{j.attempts ? ` · ${t`attempts`} ${j.attempts}` : ''}
              </p>
              {j.error ? <p className="mt-1 text-xs text-danger">{j.error}</p> : null}
              <Button variant="ghost" size="sm" className="mt-1" onClick={() => setOpenJob(openJob === j.id ? null : j.id!)}>{openJob === j.id ? t`Hide events` : t`Show events`}</Button>
              {openJob === j.id ? <JobEvents id={j.id!} /> : null}
            </li>
          ))}
        </ol>
      ) : <EmptyState title={t`No jobs yet`} />}
    </div>
  );
}

function JobEvents({ id }: { id: string }) {
  const { tenant } = useTenant();
  const { t } = useLingui();
  const { locale, tz } = useLocale();
  const q = useQuery({ queryKey: ['t', tenant.slug, 'job', id], queryFn: async () => (await api.GET('/v1/jobs/{id}', { params: { path: { id } } })).data as { events?: { at?: string; level?: string; message?: string }[] } });
  if (q.isPending) return <Skeleton className="mt-2 h-10 w-full" />;
  if (q.isError) return <p className="mt-2 text-xs text-danger">{t`Could not load job events`}</p>;
  const evs = q.data.events ?? [];
  if (!evs.length) return <p className="mt-2 text-xs text-fg-muted">{t`No events`}</p>;
  return (
    <ul className="mt-2 space-y-1 border-s border-border ps-3 text-xs">
      {evs.map((e, i) => <li key={i}><span className="text-fg-muted">{e.at ? fmtDateTime(e.at, { locale, tz }, { timeStyle: 'medium' }) : ''}</span> {e.level ? <span className="uppercase">{e.level}</span> : null} {e.message}</li>)}
    </ul>
  );
}

/** Clip editor on a Library item made from a catch-up programme: its range of the channel's recording (source
 *  {kind: programme, channel_id, start_at, end_at}). Uploaded videos have no recording to cut from. */
function AssetClipEditorButton({ a }: { a: Asset }) {
  const { tenant, scopes } = useTenant();
  const src = (a as Asset & { source?: { kind?: string; channel_id?: string; start_at?: string; end_at?: string; programme_id?: string } }).source;
  const ok = src?.kind === 'programme' && !!src.channel_id && !!src.start_at && !!src.end_at && can(scopes, 'clips:write');
  if (!ok) return null;
  return <AssetClipEditorOpen a={a} tenant={tenant.slug} channelId={src!.channel_id!} startAt={src!.start_at!} endAt={src!.end_at!} programmeId={src!.programme_id} />;
}

function AssetClipEditorOpen({ a, tenant, channelId, startAt, endAt, programmeId }: { a: Asset; tenant: string; channelId: string; startAt: string; endAt: string; programmeId?: string }) {
  const { t } = useLingui();
  const ch = useChannel(tenant, channelId);
  const [source, setSource] = React.useState<ClipEditorSource | null>(null);
  if (!ch.data) return null;
  const s = Date.parse(startAt), e = Date.parse(endAt);
  const src = { programme_id: programmeId };
  return (
    <>
      <Button size="sm" variant="secondary" data-testid="asset-clip-editor"
        onClick={() => setSource({ channel: ch.data!, startMs: s, endMs: e, programmeId: src!.programme_id, title: a.title ?? undefined, inMs: s, outMs: Math.min(e, s + 60_000) })}>
        {t`Clip editor`}
      </Button>
      <ClipEditor open={!!source} onOpenChange={(o) => { if (!o) setSource(null); }} source={source} />
    </>
  );
}
